import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Hook for managing campaign drag state and reordering logic
 * Handles drag start/end, reordering, and orphan tracking
 */
export function useCampaignDragState(topLevelSprints, orphanedNestedSprints, orphanedDetails, onReorderSprints) {
  // Drag state for campaign nesting/reordering
  const [draggedSprintId, setDraggedSprintId] = useState(null);
  
  const handleDragStart = useCallback((sprintId) => {
    // IMPORTANT: Delay the state update to avoid DOM mutations during dragstart.
    // Synchronous state updates during dragstart cause React re-renders which
    // can insert/remove DOM elements (like child drop zones), canceling the drag.
    // Using requestAnimationFrame ensures the drag operation is fully initiated
    // before any DOM changes occur.
    requestAnimationFrame(() => {
      setDraggedSprintId(sprintId);
    });
  }, []);
  
  const handleDragEnd = useCallback(() => {
    setDraggedSprintId(null);
  }, []);

  // Handle campaign reordering
  const handleReorderCampaign = useCallback((draggedId, targetId, position) => {
    if (!onReorderSprints || !topLevelSprints) return;
    
    const currentOrder = topLevelSprints.map(s => s.id);
    const draggedIndex = currentOrder.indexOf(draggedId);
    let targetIndex = currentOrder.indexOf(targetId);
    
    // If dragged item isn't in the list, it might be a nested item being promoted
    if (draggedIndex === -1) {
      const newOrder = [...currentOrder];
      if (position === 'after') {
        newOrder.splice(targetIndex + 1, 0, draggedId);
      } else {
        newOrder.splice(targetIndex, 0, draggedId);
      }
      console.log('[Sidebar] Reordering (new item):', { draggedId, targetId, position, newOrder });
      onReorderSprints(newOrder);
      return;
    }
    
    const newOrder = currentOrder.filter(id => id !== draggedId);
    targetIndex = newOrder.indexOf(targetId);
    
    if (position === 'after') {
      newOrder.splice(targetIndex + 1, 0, draggedId);
    } else {
      newOrder.splice(targetIndex, 0, draggedId);
    }
    
    console.log('[Sidebar] Reordering:', { draggedId, targetId, position, newOrder });
    onReorderSprints(newOrder);
  }, [topLevelSprints, onReorderSprints]);

  // Track orphaned campaigns for debugging (no auto-fix to prevent race conditions)
  const loggedOrphansRef = useRef(new Set());
  const orphanedCount = orphanedNestedSprints?.length || 0;
  
  useEffect(() => {
    if (orphanedCount > 0) {
      const unloggedOrphans = orphanedNestedSprints.filter(
        s => !loggedOrphansRef.current.has(s.id)
      );
      
      if (unloggedOrphans.length > 0) {
        unloggedOrphans.forEach(s => loggedOrphansRef.current.add(s.id));
        const details = (orphanedDetails || [])
          .filter(d => unloggedOrphans.some(o => o.id === d.sprint.id))
          .map(d => ({
            id: d.sprint.id,
            name: d.sprint.name,
            parentSprintId: d.sprint.parentSprintId,
            reason: d.reason,
          }));
        console.warn(
          `[SIDEBAR] ${unloggedOrphans.length} orphaned campaign(s) detected — surfaced at root until resolved:`,
          details
        );
        if (details.length > 0) {
          console.table(details);
        }
      }
    }
  }, [orphanedCount, orphanedNestedSprints, orphanedDetails]);

  return {
    draggedSprintId,
    handleDragStart,
    handleDragEnd,
    handleReorderCampaign,
  };
}

export default useCampaignDragState;
